import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { pushAPI } from '../services/api';
import {
  ensureSubscribed,
  getCurrentSubscription,
  getPermission,
  getUnavailableReason,
  isPushSupported,
  isStandalone,
  registerServiceWorker,
  requiresHomeScreenInstall,
  subscribeToPush,
  unsubscribeFromPush,
} from '../services/push';
import type { PushDevice, PushTestResult, PushUnavailableReason } from '../types/push';

interface PushNotificationContextType {
  /** Támogatja-e a böngésző a push értesítéseket. */
  supported: boolean;
  /** Kezdőképernyőre telepített appként fut-e (iOS-en ez feltétel). */
  standalone: boolean;
  /** iPhone/iPad, ahol előbb telepíteni kell az appot. */
  needsHomeScreenInstall: boolean;
  /** A böngésző értesítési engedélyének állapota. */
  permission: NotificationPermission;
  /** Fel van-e iratkozva ez az eszköz. */
  subscribed: boolean;
  /** A fiókhoz tartozó összes feliratkozott eszköz. */
  devices: PushDevice[];
  /** Miért nem kapcsolható be (null = bekapcsolható). */
  unavailableReason: PushUnavailableReason | null;
  /** Épp fut egy művelet (be-/kikapcsolás, teszt). */
  busy: boolean;
  /** Az első állapotlekérdezés még tart. */
  loading: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  /** Teszt értesítés a saját eszközökre; a teljes diagnosztikát adja vissza. */
  sendTest: () => Promise<PushTestResult>;
  removeDevice: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * A service workerből érkező visszajelzés után szórt böngésző-esemény.
 * Az oldalak erre iratkozhatnak fel, hogy újratöltsék az adataikat.
 */
export const ATTENDANCE_UPDATED_EVENT = 'clubtracker:attendance-updated';

const ATTENDANCE_ATTENDING = 1;

const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined);

export const usePushNotifications = (): PushNotificationContextType => {
  const context = useContext(PushNotificationContext);
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const supported = useMemo(() => isPushSupported(), []);
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [permission, setPermission] = useState<NotificationPermission>(() => getPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [devices, setDevices] = useState<PushDevice[]>([]);
  const [unavailableReason, setUnavailableReason] = useState<PushUnavailableReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // A service worker regisztrációja a bejelentkezéstől függetlenül megtörténik,
  // hogy a PWA telepíthető legyen és a korábbi feliratkozás életben maradjon.
  useEffect(() => {
    registerServiceWorker();

    const media = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => setStandalone(isStandalone());
    media.addEventListener('change', onDisplayModeChange);

    return () => media.removeEventListener('change', onDisplayModeChange);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setSubscribed(false);
      setDevices([]);
      setLoading(false);
      return;
    }

    try {
      setPermission(getPermission());

      const reason = await getUnavailableReason();
      setUnavailableReason(reason);

      const subscription = await getCurrentSubscription();
      setSubscribed(Boolean(subscription));

      // Az eszközlista akkor is érdekes, ha ezen a gépen nem kapcsolható be a push
      // (pl. iPhone böngészőfülön látni akarja, mely eszközei vannak feliratkozva).
      setDevices(await pushAPI.getDevices());
    } catch (error) {
      console.error('A push állapot lekérdezése sikertelen:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Bejelentkezés után: ha az engedély már megvan, csendben visszaállítjuk a
  // feliratkozást (kijelentkezéskor leiratkozunk), és átadjuk a tokent a service workernek.
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setSubscribed(false);
      setDevices([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      await ensureSubscribed();

      if (!cancelled) {
        await refresh();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, refresh]);

  // A service worker üzenetei: értesítésre kattintáskor navigálunk az appon belül.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data;

      if (data?.type === 'NOTIFICATION_CLICK' && typeof data.url === 'string') {
        navigate(data.url);
        return;
      }

      // Az értesítés "Jövök" / "Nem jövök" gombjáról érkezett válasz: a nyitott
      // oldalak frissíthetik magukat, hogy ne maradjon elavult adat a képernyőn.
      if (data?.type === 'ATTENDANCE_UPDATED') {
        showToast(
          data.status === ATTENDANCE_ATTENDING
            ? 'Válaszod rögzítve: Jövök.'
            : 'Válaszod rögzítve: Nem jövök.',
          'info'
        );
        window.dispatchEvent(
          new CustomEvent(ATTENDANCE_UPDATED_EVENT, {
            detail: { eventId: data.eventId, status: data.status },
          })
        );
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, showToast]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setPermission(getPermission());
      setSubscribed(true);
      setUnavailableReason(null);
      setDevices(await pushAPI.getDevices());
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      setDevices(await pushAPI.getDevices());
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    try {
      return await pushAPI.sendTest();
    } finally {
      setBusy(false);
    }
  }, []);

  const removeDevice = useCallback(
    async (id: number) => {
      setBusy(true);
      try {
        await pushAPI.deleteDevice(id);

        // Ha a saját eszközünket töröltük, a helyi feliratkozást is bontjuk
        const current = await getCurrentSubscription();
        const removed = devices.find((device) => device.id === id);
        if (current && removed && current.endpoint === removed.endpoint) {
          await current.unsubscribe();
          setSubscribed(false);
        }

        setDevices(await pushAPI.getDevices());
      } finally {
        setBusy(false);
      }
    },
    [devices]
  );

  const value: PushNotificationContextType = {
    supported,
    standalone,
    needsHomeScreenInstall: requiresHomeScreenInstall(),
    permission,
    subscribed,
    devices,
    unavailableReason,
    busy,
    loading,
    enable,
    disable,
    sendTest,
    removeDevice,
    refresh,
  };

  return <PushNotificationContext.Provider value={value}>{children}</PushNotificationContext.Provider>;
};
